// Tax File Numbers.
//
// In the shared package because the browser should refuse an obviously wrong
// TFN before sending it anywhere, and the server must refuse it again — and
// those two checks have to be the same check.
//
// ---------------------------------------------------------------------------
// The checksum is the point
// ---------------------------------------------------------------------------
// A TFN carries a weighted modulus-11 check digit. Validating it catches the
// overwhelming majority of typos and transpositions at the moment of entry,
// which matters more here than almost anywhere else: a wrong TFN is not
// rejected by the ATO with a helpful message. It belongs to somebody else, or
// to nobody, and the error surfaces weeks later as a failed lodgement.
//
// This does NOT prove a TFN is real or belongs to the person. It proves the
// digits were typed correctly.
/** The ATO's published weighting for the modulus-11 check. */
const WEIGHTS = [1, 4, 3, 7, 5, 8, 6, 9, 10];
/** Strip everything that is not a digit. TFNs are written "123 456 789". */
export function normaliseTfn(input) {
    return input.replace(/\D/g, '');
}
/**
 * Validate a TFN.
 *
 * Accepts 8 or 9 digits: TFNs issued before 1988 are eight digits long and
 * are still in circulation, so rejecting them would turn away real clients.
 */
export function checkTfn(input) {
    const digits = normaliseTfn(input);
    if (digits.length === 0) {
        return { ok: false, normalised: '', problem: 'empty', message: 'Enter a tax file number.' };
    }
    if (digits.length !== 8 && digits.length !== 9) {
        return {
            ok: false,
            normalised: digits,
            problem: 'length',
            message: 'A tax file number is 9 digits (or 8 for older ones).',
        };
    }
    // An 8-digit TFN is weighted as though it had a leading zero.
    const padded = digits.padStart(9, '0');
    let total = 0;
    for (const [i, weight] of WEIGHTS.entries()) {
        total += Number(padded[i]) * weight;
    }
    if (total % 11 !== 0) {
        return {
            ok: false,
            normalised: digits,
            problem: 'checksum',
            message: 'That tax file number does not look right — please check the digits.',
        };
    }
    return { ok: true, normalised: digits };
}
/**
 * How a TFN is shown on screen: last three digits only.
 *
 * Enough to confirm you are looking at the right client, useless to anybody
 * reading over a shoulder or looking at a screenshot. Every surface that
 * displays a TFN uses this; the full value is revealed only by an explicit,
 * audited action. See docs/decisions/0003-storing-tfns.md.
 */
export function maskTfn(tfn) {
    const digits = normaliseTfn(tfn);
    if (digits.length < 3)
        return '•••  •••  •••';
    return `•••  •••  ${digits.slice(-3)}`;
}
/** Group a TFN as the ATO prints it: 123 456 789. */
export function formatTfn(tfn) {
    const digits = normaliseTfn(tfn);
    if (digits.length !== 9)
        return digits;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}
/**
 * Validate a date of birth.
 *
 * Takes the ISO value an `<input type="date">` produces. The bounds are
 * deliberately loose — a 104-year-old client is unusual, not impossible — but
 * a future date or a 19th-century one is a typo every time.
 */
export function checkDob(input, now = new Date()) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
    if (!match) {
        return { ok: false, normalised: '', message: 'Enter a date of birth.' };
    }
    const [, year, month, day] = match;
    const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
    // Catches 31 February, which Date would otherwise roll into March.
    if (Number.isNaN(date.getTime()) ||
        date.getUTCFullYear() !== Number(year) ||
        date.getUTCMonth() + 1 !== Number(month) ||
        date.getUTCDate() !== Number(day)) {
        return { ok: false, normalised: '', message: 'That date does not exist.' };
    }
    if (date.getTime() > now.getTime()) {
        return { ok: false, normalised: '', message: 'A date of birth cannot be in the future.' };
    }
    const age = (now.getTime() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    if (age > 120) {
        return { ok: false, normalised: '', message: 'Please check the year.' };
    }
    return { ok: true, normalised: `${year}-${month}-${day}` };
}
/** Date of birth on screen: the year only, which is enough to tell clients apart. */
export function maskDob(iso) {
    const year = iso.slice(0, 4);
    return /^\d{4}$/.test(year) ? `••/••/${year}` : '••/••/••••';
}
//# sourceMappingURL=tfn.js.map